// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Finance & tax rules  (commercial rules engine: client + the pure maths both the customer bill and the admin screen use)
// GET|PUT /admin/commercial-rules (staff) and GET /public/commercial-rules (what a customer's bill depends on). ENFORCED on the customer side: the standard delivery fee and the free-delivery
// threshold (`deliveryFeeFor`, used by the cart and checkout, with a delivery zone's own fee / threshold taking precedence). RECORDED: business entity + GSTIN, GST slabs, packing / bulk fees, courier payouts.
// ZenFresh ships NO statutory GST rates — every rate is entered by staff. This file only does arithmetic and format checks: GSTIN (state code, PAN pattern, official check character), GST out of a
// tax-INCLUSIVE price, and the CGST/SGST vs IGST split. Menu prices are final prices, so tax is always extracted from them, never added on top.
// Neighbours: pages/admin/AdminFinance, hooks/useCommercialRules, pages/Checkout, components/CartDrawer, utils/zones, data/seafood (categories).
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed, apiGet } from "./api";
import { DELIVERY_FEE } from "../data/delivery";
import type { SeafoodCategory } from "../data/seafood";
import type { PublicZone } from "./zones";

export interface Slab { id: string; hsn: string; name: string; categories: SeafoodCategory[]; rate: number; note: string }
export interface Rules {
  business: { legalName: string; gstin: string; invoicePrefix: string; reverseCharge: boolean };
  gst: { slabs: Slab[] };
  delivery: { standardFee: number; freeAbove: number | null };
  packaging: { packingFee: number; bulkAboveKg: number | null; bulkFee: number };
  payouts: { baseDrop: number; minPayout: number | null };
}
export interface SavedRules extends Rules { configured: boolean; updatedBy: string | null; updatedAt: string | null }
export interface PublicRules { deliveryFee: number; freeDeliveryAbove: number | null }

export const fetchRules = () => apiAuthed<SavedRules>("GET", "/admin/commercial-rules");
export const saveRules = (rules: Rules) => apiAuthed<SavedRules>("PUT", "/admin/commercial-rules", rules);
export const fetchPublicRules = () => apiGet<PublicRules>("/public/commercial-rules");
/** What the site charged before any rule was saved. */
export const DEFAULT_PUBLIC_RULES: PublicRules = { deliveryFee: DELIVERY_FEE, freeDeliveryAbove: null };

/** Delivery fee for a basket: free above the threshold (the zone's own, else the site-wide one), otherwise the zone's fee, else the standard fee. */
export function deliveryFeeFor(subtotal: number, zone: PublicZone | null, rules: PublicRules): number {
  const freeAbove = zone?.freeAbove ?? rules.freeDeliveryAbove;
  if (freeAbove !== null && subtotal >= freeAbove) return 0;
  return zone?.deliveryFee ?? rules.deliveryFee;
}

// ── GST maths ────────────────────────────────────────────────────────────────
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
/** GST contained in a tax-INCLUSIVE amount at `rate` percent, and the value before tax. */
export function gstInclusive(amount: number, rate: number) {
  const tax = round2((amount * rate) / (100 + rate));
  return { taxable: round2(amount - tax), tax };
}
/** Intra-state supply splits the tax equally into CGST + SGST; inter-state is all IGST. The two halves always add back to the tax. */
export function splitTax(tax: number, intraState: boolean) {
  if (!intraState) return { cgst: 0, sgst: 0, igst: tax };
  const cgst = round2(tax / 2);
  return { cgst, sgst: round2(tax - cgst), igst: 0 };
}
export const rateFor = (rules: Pick<Rules, "gst">, category: SeafoodCategory): number | null => rules.gst.slabs.find((s) => s.categories.includes(category))?.rate ?? null;
export const slabFor = (rules: Pick<Rules, "gst">, category: SeafoodCategory): Slab | null => rules.gst.slabs.find((s) => s.categories.includes(category)) ?? null;

// ── GSTIN ────────────────────────────────────────────────────────────────────
export const STATE_CODES: Record<string, string> = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim",
  "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur", "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal", "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh",
  "23": "Madhya Pradesh", "24": "Gujarat", "26": "Dadra & Nagar Haveli and Daman & Diu", "27": "Maharashtra", "29": "Karnataka", "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry",
  "35": "Andaman & Nicobar Islands", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh", "97": "Other Territory", "99": "Centre Jurisdiction",
};
const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const GSTIN_RE = /^(\d{2})([A-Z]{5}\d{4}[A-Z])([1-9A-Z])(Z)([0-9A-Z])$/;
/** Why this isn't a valid GSTIN, or null (mirrors backend domain/finance.gstin_problem). */
export function gstinProblem(value: string): string | null {
  const g = value.trim().toUpperCase();
  const m = GSTIN_RE.exec(g);
  if (!m) return "A GSTIN is 15 characters: state code, PAN, entity number, Z, check character.";
  if (!STATE_CODES[m[1]]) return `${m[1]} is not a GST state code.`;
  let sum = 0;
  for (let i = 0; i < 14; i++) { const p = ALPHABET.indexOf(g[i]) * (i % 2 === 0 ? 1 : 2); sum += Math.floor(p / 36) + (p % 36); }
  return ALPHABET[(36 - (sum % 36)) % 36] === g[14] ? null : "The last character doesn't match: check the GSTIN for a typing mistake.";
}
export const gstinState = (value: string) => STATE_CODES[value.slice(0, 2)] ?? null;

/** HSN headings staff may need (classification only — the RATE for each is entered by staff with their tax advisor). */
export const HSN_SUGGESTIONS = [
  { hsn: "0302", label: "Fish, fresh or chilled (whole)" },
  { hsn: "0303", label: "Fish, frozen (whole)" },
  { hsn: "0304", label: "Fish fillets and other fish meat" },
  { hsn: "0306", label: "Crustaceans (prawns, crabs, lobsters)" },
  { hsn: "0307", label: "Molluscs (squid, mussels, oysters)" },
  { hsn: "1604", label: "Prepared or preserved fish" },
  { hsn: "1605", label: "Prepared crustaceans and molluscs" },
] as const;
